import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Rates", path: "/rates" },
  { name: "Pricing", path: "/pricing" },
  { name: "Referral", path: "/referral" },
  { name: "Contact", path: "/contact" },
  { name: "About", path: "/about" },
];

const defaultBranding = {
  websiteLogoUrl: "/voiphub_fixed_transparent.png",
  portalLogoUrl: "/portal/voiphub_fixed_transparent.png",
};

const Header = ({ websiteLogoUrl }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass shadow-lg py-2" : "bg-transparent py-4"
      }`}
      data-testid="header"
    >
      <div className="container-custom flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src={websiteLogoUrl} alt="VoipHub" className="h-12 w-auto drop-shadow-[0_12px_24px_rgba(232,107,107,0.18)]" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-medium transition-colors link-underline ${
                location.pathname === link.path
                  ? "text-brand-500"
                  : "text-slate-700 hover:text-brand-500"
              }`}
              data-testid={`nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://t.me/voiphub_support"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-600 hover:text-brand-500 transition-colors"
            data-testid="telegram-btn"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Telegram</span>
          </a>
          <a href="/portal/">
            <Button variant="outline" className="rounded-full px-5 py-2 border-slate-300 text-slate-700 hover:border-brand-400 hover:text-brand-500 text-sm font-semibold" data-testid="login-btn">
              Login
            </Button>
          </a>
          <a href="/portal/">
            <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-5 py-2 text-sm font-semibold shadow-sm" data-testid="signup-btn">
              Sign Up Free
            </Button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-3 px-4 font-medium rounded-lg ${
                    location.pathname === link.path
                      ? "text-brand-500 bg-brand-25"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t mt-4 pt-4 space-y-3">
                <a
                  href="https://t.me/voiphub_support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 py-3 px-4 text-slate-600"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat on Telegram
                </a>
                <a href="/portal/" className="block">
                  <Button variant="outline" className="w-full rounded-full border-slate-300 text-slate-700">
                    Login
                  </Button>
                </a>
                <a href="/portal/" className="block">
                  <Button className="w-full bg-brand-500 hover:bg-brand-600 text-white rounded-full">
                    Sign Up Free
                  </Button>
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = ({ websiteLogoUrl }) => {
  return (
    <footer className="bg-slate-900 text-white noise-bg" data-testid="footer">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center mb-6">
              <img src={websiteLogoUrl} alt="VoipHub" className="h-12 w-auto drop-shadow-[0_12px_24px_rgba(232,107,107,0.18)]" />
            </Link>
            <p className="text-slate-400 mb-6">
              Premium VoIP services with competitive rates for 235+ countries worldwide.
            </p>
            <div className="flex gap-4">
              <a
                href="https://t.me/voiphub_support"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-brand-500 transition-colors"
                data-testid="footer-telegram"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-brand-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6">Services</h4>
            <ul className="space-y-3 text-slate-400">
              <li>VoIP/SIP Accounts</li>
              <li>Hosted PBX</li>
              <li>A-Z Voice Termination</li>
              <li>Call Center Routes</li>
              <li>Wholesale Voice</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-slate-400">
                <Mail className="w-5 h-5 text-brand-400" />
                <a href="mailto:support@voiphub.in" className="hover:text-white transition-colors">
                  support@voiphub.in
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Phone className="w-5 h-5 text-brand-400" />
                <a href="tel:+91XXXXXXXXXX" className="hover:text-white transition-colors">
                  +91-XXXXXXXXXX
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <MessageCircle className="w-5 h-5 text-brand-400" />
                <a
                  href="https://t.me/voiphub_support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  @voiphub_support
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} VoipHub. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/faq" className="text-slate-400 hover:text-white transition-colors">
              FAQ
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  const [branding, setBranding] = useState(defaultBranding);

  useEffect(() => {
    let isActive = true;

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const settingsUrl = apiBase ? `${apiBase}/api/settings/public` : "/api/settings/public";

    fetch(settingsUrl)
      .then((response) => response.json())
      .then((payload) => {
        if (!isActive) return;
        const data = payload?.data || payload || {};
        setBranding({
          websiteLogoUrl: data?.branding?.websiteLogoUrl || defaultBranding.websiteLogoUrl,
          portalLogoUrl: data?.branding?.portalLogoUrl || defaultBranding.portalLogoUrl,
        });
      })
      .catch(() => {
        if (!isActive) return;
        setBranding(defaultBranding);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header websiteLogoUrl={branding.websiteLogoUrl} />
      <main className="flex-1 pt-20">
        <Outlet />
      </main>
      <Footer websiteLogoUrl={branding.websiteLogoUrl} />
    </div>
  );
};

export default Layout;
